import React, { useState, useEffect } from 'react';
import { supabase } from '@/supabaseClient';
import { User } from '@/types';
import { 
  Database, User as UserIcon, Calendar, Sparkles, TrendingUp, DollarSign, Clock, RefreshCw, Loader2, AlertTriangle, Users
} from 'lucide-react';

interface IaUsageReportProps {
  users: User[];
}

interface UsageLog {
  id: string;
  user_id: string | null;
  query: string;
  input_tokens: number;
  output_tokens: number;
  cost: number;
  created_at: string;
  users: {
    full_name: string;
    email: string;
  } | null;
}

interface UserSummary {
  userId: string;
  name: string;
  email: string;
  totalCost: number;
  totalRequests: number;
  totalTokens: number;
}

export const IaUsageReport: React.FC<IaUsageReportProps> = ({ users }) => {
  const [logs, setLogs] = useState<UsageLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<UsageLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'7d' | '30d' | 'month' | 'all'>('30d');

  const fetchLogs = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from('ia_usage_logs')
        .select(`
          id,
          user_id,
          query,
          input_tokens,
          output_tokens,
          cost,
          created_at,
          users (
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      if (fetchErr) throw fetchErr;
      
      // Ajuste de tipagem para o Supabase Join
      const formattedData = (data || []).map((item: any) => ({
        ...item,
        users: Array.isArray(item.users) ? item.users[0] : item.users
      })) as UsageLog[];

      setLogs(formattedData);
    } catch (err: any) {
      console.error("Erro ao buscar logs de consumo da IA:", err);
      setError("Não foi possível carregar os dados de consumo: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // Filtragem por período
  useEffect(() => {
    if (logs.length === 0) {
      setFilteredLogs([]);
      return;
    }

    const now = new Date();
    let filterDate = new Date();

    if (period === '7d') {
      filterDate.setDate(now.getDate() - 7);
    } else if (period === '30d') {
      filterDate.setDate(now.getDate() - 30);
    } else if (period === 'month') {
      filterDate = new Date(now.getFullYear(), now.getMonth(), 1); // Primeiro dia do mês atual
    } else {
      setFilteredLogs(logs);
      return;
    }

    const filtered = logs.filter(log => new Date(log.created_at) >= filterDate);
    setFilteredLogs(filtered);
  }, [logs, period]);

  // Cálculos consolidados
  const totalCost = filteredLogs.reduce((sum, log) => sum + Number(log.cost), 0);
  const totalRequests = filteredLogs.length;
  const totalInputTokens = filteredLogs.reduce((sum, log) => sum + log.input_tokens, 0);
  const totalOutputTokens = filteredLogs.reduce((sum, log) => sum + log.output_tokens, 0);
  const totalTokens = totalInputTokens + totalOutputTokens;

  // Ranking de usuários
  const userSummaryMap: Record<string, UserSummary> = {};

  // Inicializar todos os usuários conhecidos da prop para mostrar no ranking (mesmo os com 0 uso)
  users.forEach(u => {
    userSummaryMap[u.id] = {
      userId: u.id,
      name: u.fullName,
      email: u.email,
      totalCost: 0,
      totalRequests: 0,
      totalTokens: 0
    };
  });

  // Preencher dados reais
  filteredLogs.forEach(log => {
    const userId = log.user_id || 'anonymous';
    if (!userSummaryMap[userId]) {
      userSummaryMap[userId] = {
        userId,
        name: log.users?.full_name || 'Usuário Removido',
        email: log.users?.email || 'N/A',
        totalCost: 0,
        totalRequests: 0,
        totalTokens: 0
      };
    }
    userSummaryMap[userId].totalCost += Number(log.cost);
    userSummaryMap[userId].totalRequests += 1;
    userSummaryMap[userId].totalTokens += (log.input_tokens + log.output_tokens);
  });

  // Converter para array e ordernar por custo decrescente
  const topUsers = Object.values(userSummaryMap)
    .filter(u => u.totalCost > 0 || u.totalRequests > 0)
    .sort((a, b) => b.totalCost - a.totalCost);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Título e Ações */}
      <div className="flex justify-between items-center bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
        <div>
          <h3 className="font-black text-slate-800 uppercase tracking-tight flex items-center gap-2">
            <TrendingUp className="text-blue-600" size={20} />
            Consumo e Gastos com IA Claude
          </h3>
          <p className="text-slate-500 text-sm">Acompanhe detalhadamente o uso de tokens e custos estimados por período.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-slate-100 p-1.5 rounded-xl border border-slate-200 inline-flex gap-1">
            <button 
              onClick={() => setPeriod('7d')} 
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${period === '7d' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              7 Dias
            </button>
            <button 
              onClick={() => setPeriod('30d')} 
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${period === '30d' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              30 Dias
            </button>
            <button 
              onClick={() => setPeriod('month')} 
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${period === 'month' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Este Mês
            </button>
            <button 
              onClick={() => setPeriod('all')} 
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${period === 'all' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Tudo
            </button>
          </div>
          <button 
            onClick={fetchLogs} 
            disabled={isLoading}
            className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors text-slate-600 disabled:opacity-50"
            title="Atualizar dados"
          >
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20 bg-white rounded-3xl border border-slate-200 shadow-sm">
          <Loader2 className="animate-spin text-blue-600" size={32} />
        </div>
      ) : error ? (
        <div className="p-8 bg-red-50 border border-red-100 text-red-700 rounded-3xl flex items-center gap-3">
          <AlertTriangle size={24} />
          <p className="font-medium">{error}</p>
        </div>
      ) : (
        <>
          {/* Métricas Consolidadas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-3xl shadow-md space-y-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-blue-100">Custo Estimado ({period.toUpperCase()})</span>
              <div className="text-3xl font-black flex items-baseline gap-1">
                <span className="text-lg font-bold text-blue-200">$</span>
                {totalCost.toFixed(2)}
              </div>
              <p className="text-xs text-blue-100/80">Projetado sobre a tarifa do modelo Haiku 4.5</p>
            </div>
            
            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total de Requisições</span>
              <div className="text-3xl font-black text-slate-800">{totalRequests}</div>
              <p className="text-xs text-slate-400">Total de interações com a IA no período</p>
            </div>

            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total de Tokens Usados</span>
              <div className="text-3xl font-black text-slate-800">{totalTokens.toLocaleString()}</div>
              <p className="text-xs text-slate-400">Entrada + Saída combinados</p>
            </div>

            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Média por Requisição</span>
              <div className="text-3xl font-black text-slate-800">
                ${totalRequests > 0 ? (totalCost / totalRequests).toFixed(4) : '0.0000'}
              </div>
              <p className="text-xs text-slate-400">Custo médio por pergunta enviada</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* RANKING DE CONSUMO POR USUÁRIO */}
            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden flex flex-col">
              <div className="p-6 border-b bg-slate-50/50">
                <h4 className="font-black text-slate-800 text-sm uppercase tracking-wider flex items-center gap-2">
                  <Users size={16} className="text-blue-500" />
                  Quem gasta mais
                </h4>
                <p className="text-xs text-slate-400 mt-1">Ranking de consumo por usuário no período selecionado.</p>
              </div>
              <div className="divide-y divide-slate-100 overflow-y-auto max-h-[450px] flex-1">
                {topUsers.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-xs font-bold">Sem registros no período.</div>
                ) : (
                  topUsers.map((u, i) => (
                    <div key={u.userId} className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                          {i + 1}
                        </div>
                        <div>
                          <p className="font-bold text-slate-700 text-sm leading-none mb-1">{u.name}</p>
                          <p className="text-[10px] text-slate-400 font-bold uppercase">{u.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-slate-800 text-sm">${u.totalCost.toFixed(2)}</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">{u.totalRequests} reqs • {u.totalTokens.toLocaleString()} tkn</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* LOGS RECENTES */}
            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden lg:col-span-2 flex flex-col">
              <div className="p-6 border-b bg-slate-50/50">
                <h4 className="font-black text-slate-800 text-sm uppercase tracking-wider flex items-center gap-2">
                  <Database size={16} className="text-blue-500" />
                  Logs Recentes de Chamadas
                </h4>
                <p className="text-xs text-slate-400 mt-1">Lista com os últimos registros de consumo armazenados no banco.</p>
              </div>
              <div className="overflow-x-auto flex-1 max-h-[450px]">
                {filteredLogs.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-xs font-bold">Nenhum log registrado para este período.</div>
                ) : (
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b bg-slate-50/30 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                        <th className="px-6 py-4">Usuário</th>
                        <th className="px-6 py-4">Pergunta (Início)</th>
                        <th className="px-6 py-4 text-center">Tokens (In/Out)</th>
                        <th className="px-6 py-4 text-right">Custo</th>
                        <th className="px-6 py-4 text-right">Data/Hora</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {filteredLogs.slice(0, 30).map((log) => (
                        <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-bold text-slate-700">
                            {log.users?.full_name || 'Anônimo'}
                          </td>
                          <td className="px-6 py-4 text-slate-500 max-w-[200px] truncate" title={log.query}>
                            {log.query || 'Sem texto'}
                          </td>
                          <td className="px-6 py-4 text-center font-medium text-slate-500">
                            {log.input_tokens} / {log.output_tokens}
                          </td>
                          <td className="px-6 py-4 text-right font-black text-slate-800">
                            ${Number(log.cost).toFixed(4)}
                          </td>
                          <td className="px-6 py-4 text-right text-slate-400 font-medium">
                            {new Date(log.created_at).toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
